import React from "react";

import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import {
  CheckIcon,
  colors,
  RemoveCircleIcon,
  WarningIcon
} from "@dotnet/telematics-design-system";

import {
  ACTIVITY_STATUS,
  ANALYSIS_PROVIDERS_STATUS,
  API_CREDENTIAL_STATUS,
  COLUMN_RESOURCES,
  COMPANY_CARD_STATUS,
  DOWNLOAD_ACTIVITY_STATUS,
  ICON_DEFAULT_STYLE,
  INTEGRATION_STATUS,
  INVITATION_STATUS,
  LAST_DOWNLOAD_STATUS,
  ROUTES
} from "../../shared";
import Badge, { BadgeData, BadgeDataWithIcon } from "../Badge/Badge";
import { TooltipSizes } from "../Tooltip/types";

type SharedStatusResourceType =
  | ACTIVITY_STATUS
  | DOWNLOAD_ACTIVITY_STATUS
  | COMPANY_CARD_STATUS
  | INVITATION_STATUS
  | LAST_DOWNLOAD_STATUS
  | INTEGRATION_STATUS
  | API_CREDENTIAL_STATUS
  | ANALYSIS_PROVIDERS_STATUS;

interface ISharedStatusBadge {
  status: SharedStatusResourceType;
  badgeText?: string;
  resourceName?: COLUMN_RESOURCES;
  tooltipSize?: TooltipSizes;
  bubbleOnly?: boolean;
  customTooltipText?: string;
  showTooltip?: boolean;
  iconLeft?: React.ReactNode | React.JSX.Element;
  isDisabled?: boolean;
}

const SharedStatusBadge = ({
  status,
  badgeText,
  resourceName,
  tooltipSize,
  bubbleOnly,
  customTooltipText,
  showTooltip = true,
  iconLeft,
  isDisabled
}: ISharedStatusBadge) => {
  const { t } = useTranslation();

  const location = useLocation();

  const isIntegrationsPage = location.pathname === ROUTES.Integrations;
  const isIntegrationsApiPage = location.pathname === ROUTES.IntegrationsApi;
  const isAnalysisProvidersPage =
    location.pathname === ROUTES.AnalysisProviders;

  const getIntegrationsBadgeData = (
    statusName: SharedStatusResourceType
  ): BadgeDataWithIcon => {
    switch (statusName) {
      case INTEGRATION_STATUS.Active:
        return {
          color: "success",
          isInverted: false,
          icon: <CheckIcon style={ICON_DEFAULT_STYLE} />
        };

      case INTEGRATION_STATUS.Interrupted:
        return {
          color: "warning",
          isInverted: false,
          icon: (
            <WarningIcon style={ICON_DEFAULT_STYLE} color={colors.orange600} />
          )
        };

      case INTEGRATION_STATUS.Disabled:
        return { color: "neutral", isInverted: false };

      case INTEGRATION_STATUS.Failed:
      case INTEGRATION_STATUS.InvalidConfiguration:
        return {
          color: "critical",
          isInverted: false,
          icon: <RemoveCircleIcon style={ICON_DEFAULT_STYLE} />
        };

      default:
        return { color: "light", isInverted: false };
    }
  };

  const getApiCredentialBadgeData = (
    statusName: SharedStatusResourceType
  ): BadgeDataWithIcon => {
    switch (statusName) {
      case API_CREDENTIAL_STATUS.Active:
        return {
          color: "success",
          isInverted: true
        };

      case API_CREDENTIAL_STATUS.Expired:
        return {
          color: "critical",
          isInverted: true
        };

      case API_CREDENTIAL_STATUS.Disabled:
        return { color: "neutral", isInverted: false };

      default:
        return { color: "light", isInverted: false };
    }
  };

  const getBadgeData = (statusName: SharedStatusResourceType): BadgeData => {
    switch (statusName) {
      case ACTIVITY_STATUS.Active:
      case ACTIVITY_STATUS.Online:
      case COMPANY_CARD_STATUS.Online:
      case DOWNLOAD_ACTIVITY_STATUS.Successful:
        return { color: "success", isInverted: true };

      case COMPANY_CARD_STATUS.Deactivated:
      case ACTIVITY_STATUS.Deactivated:
        return { color: "neutral", isInverted: false };

      case ACTIVITY_STATUS.Inactive:
      case COMPANY_CARD_STATUS.Inactive:
      case DOWNLOAD_ACTIVITY_STATUS.Awaiting:
      case INVITATION_STATUS.Pending:
        return { color: "light", isInverted: false };

      case INVITATION_STATUS.Expired:
      case ACTIVITY_STATUS.Disabled:
      case ACTIVITY_STATUS.Offline:
      case COMPANY_CARD_STATUS.Offline:
        return { color: "warning", isInverted: true };

      case DOWNLOAD_ACTIVITY_STATUS.Failed:
        return { color: "critical", isInverted: true };

      case LAST_DOWNLOAD_STATUS.Safe:
        return { color: "success", isInverted: false };

      case LAST_DOWNLOAD_STATUS.Warning:
        return { color: "warning", isInverted: false };

      case LAST_DOWNLOAD_STATUS.Overdue:
        return { color: "critical", isInverted: false };

      default:
        return { color: "light", isInverted: false };
    }
  };

  const getBubbleData = (statusName: SharedStatusResourceType): BadgeData => {
    switch (statusName) {
      case COMPANY_CARD_STATUS.Online:
        return { color: "success", isInverted: true };
      case COMPANY_CARD_STATUS.Expired:
        return { color: "gray", isInverted: true };
      case COMPANY_CARD_STATUS.Inactive:
        return { color: "gray", isInverted: false };
      case COMPANY_CARD_STATUS.Offline:
        return { color: "warning", isInverted: true };

      default:
        return { color: "light", isInverted: false };
    }
  };

  const getBadgeTooltipText = (
    statusName: SharedStatusResourceType
  ): string => {
    if (customTooltipText || customTooltipText === "") {
      return customTooltipText;
    }

    return (
      statusName &&
      t(`General##badge##${statusName.toLowerCase()} badge tooltip text`)
    );
  };

  const resourceFunction = (
    status: SharedStatusResourceType
  ): BadgeDataWithIcon => {
    if (isIntegrationsPage || isAnalysisProvidersPage) {
      return getIntegrationsBadgeData(status);
    }

    if (isIntegrationsApiPage) {
      return getApiCredentialBadgeData(status);
    }

    if (bubbleOnly) {
      return getBubbleData(status);
    }

    return getBadgeData(status);
  };

  const color = resourceFunction(status).color;
  const isInverted = resourceFunction(status).isInverted;
  const iconComponent = resourceFunction(status)?.icon;
  const name = bubbleOnly ? "" : t(`General##${status}`);

  const statusTooltip =
    status === COMPANY_CARD_STATUS.Expired
      ? COMPANY_CARD_STATUS.Deactivated
      : status;

  const tooltipText = bubbleOnly ? statusTooltip : getBadgeTooltipText(status);

  const hasIconComponent = iconComponent ?? iconLeft;

  return (
    <Badge
      color={color}
      inverted={isInverted}
      tooltipText={
        resourceName !== COLUMN_RESOURCES.LastDownload &&
        !isIntegrationsPage &&
        !isIntegrationsApiPage &&
        !isAnalysisProvidersPage &&
        showTooltip
          ? tooltipText
          : undefined
      }
      tooltipSize={tooltipSize ?? undefined}
      bubbleOnly={bubbleOnly}
      iconLeft={hasIconComponent}
      customStyle={isDisabled && { opacity: 0.6 }}
    >
      {badgeText ?? name}
    </Badge>
  );
};

export default SharedStatusBadge;
